import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { faHourglassClock } from '@fortawesome/pro-light-svg-icons';
import { Flex, Row, Table } from 'antd';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekday from 'dayjs/plugin/weekday';
import PosText from 'pos-client/components/shared/General/PosText.tsx';
import { DARK_GREY } from 'pos-client/utils/colours.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { UserShiftModel } from 'pos-shared/models/userSchema.ts';

import { UserShiftCard } from './UserShiftCard.tsx';

dayjs.extend(isoWeek);
dayjs.extend(weekday);

export type DayName =
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday'
  | 'Sunday';

export type UserShiftGroupedRow = {
  key: string;
  week: number;
  weekStart: string;
  weekTotalTime: string | null;
  _dateMap?: Record<DayName, string>;
} & Record<DayName, UserShiftModel[]>;

type DateMap = Record<DayName, string>;

type WeekDataRow = {
  key: string;
  week: number;
  weekStart: string;
  weekTotalTime: string | null;
  _dateMap: DateMap;
} & Record<DayName, UserShiftModel[]>;

type UserShiftProps = {
  userId: string | undefined;
};

const daysOfWeek = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const UserShift = ({ userId }: UserShiftProps) => {
  const { t } = useTranslation();

  const getTotalShiftTime = (items: UserShiftModel[]) => {
    const totalMinutes = items.reduce((sum, item) => {
      if (item.type === 'Shift') {
        return sum + dayjs(item.end).diff(dayjs(item.start), 'minute');
      }
      return sum;
    }, 0);

    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return totalMinutes > 0 ? `${h}h ${m}m` : null;
  };

  const getDateMapForWeek = useMemo(
    () =>
      (monday: dayjs.Dayjs): DateMap => {
        return Object.fromEntries(
          daysOfWeek.map((day, i) => [
            day,
            monday.add(i, 'day').format('DD-MM-YYYY'),
          ])
        ) as DateMap;
      },
    []
  );

  const groupByWeekAndDay = useMemo(
    () =>
      (data: UserShiftModel[]): WeekDataRow[] => {
        const weekMap = new Map<
          number,
          {
            days: Record<DayName, UserShiftModel[]>;
            firstDate: string;
            dateMap: DateMap;
          }
        >();

        data.forEach((item) => {
          const start = dayjs(item.start).utc();
          const week = start.isoWeek();
          const day = start.format('dddd') as DayName;

          if (!weekMap.has(week)) {
            const monday = start.startOf('isoWeek');
            weekMap.set(week, {
              days: daysOfWeek.reduce(
                (acc, d) => ({ ...acc, [d]: [] }),
                {} as Record<DayName, UserShiftModel[]>
              ),
              firstDate: item.start,
              dateMap: getDateMapForWeek(monday),
            });
          }

          const weekData = weekMap.get(week)!;
          weekData.days[day].push(item);

          if (dayjs(item.start).isBefore(weekData.firstDate)) {
            weekData.firstDate = item.start;
          }
        });

        return Array.from(weekMap.entries()).map(
          ([week, { days, firstDate, dateMap }]) => {
            const allItems = Object.values(days).flat();
            const weekTotalTime = getTotalShiftTime(allItems);

            return {
              key: `week-${week}`,
              week,
              weekStart: dayjs(firstDate).format('DD-MM-YYYY'),
              weekTotalTime,
              ...days,
              _dateMap: dateMap,
            };
          }
        );
      },
    [getDateMapForWeek]
  );

  if (!userId) {
    return null;
  }

  const { data: userShift } = trpc.getUserShift.useQuery({
    id: userId,
    pageSize: 100,
    shiftStartDate: dayjs().startOf('week').toISOString(),
  });

  const columns = [
    {
      title: t('users.shift.weekCommencing'),
      dataIndex: 'week',
      key: 'week',
      width: '120px',
      align: 'center' as const,
      render: (_: unknown, record: UserShiftGroupedRow) => (
        <>
          <PosText strong>{record.weekStart}</PosText>
          <Row>
            <PosText icon={faHourglassClock} iconColor={DARK_GREY}>
              {record.weekTotalTime || '-'}
            </PosText>
          </Row>
        </>
      ),
    },
    ...daysOfWeek.map((day) => ({
      title: day,
      dataIndex: day,
      key: day,
      width: '220px',
      align: 'center' as const,
      render: (shifts: UserShiftModel[], record: UserShiftGroupedRow) => {
        return (
          <UserShiftCard
            shiftData={shifts}
            weekData={record}
            day={day as DayName}
          />
        );
      },
    })),
  ];

  return (
    <>
      <Flex vertical gap="middle" style={{ height: '100%' }}>
        <Table<UserShiftGroupedRow>
          bordered
          pagination={false}
          size="large"
          dataSource={groupByWeekAndDay(userShift?.results || [])}
          columns={columns}
          scroll={{ x: 'max-content' }}
        />
      </Flex>
    </>
  );
};
